/**
 * AssessDocumentReview.tsx — app-connected container for the department
 * assessment view. Parallel to StaticDocumentReview, but the right pane is the
 * AssessmentPanel (department filter + search + comment) instead of NotePanel,
 * and document comments render in the widened department column. The reviewer's
 * saved notes and note comments show read-only in the notes gutter (col 2) —
 * there is no NotePanel here, so nothing can edit them.
 *
 * Pipeline: parse(contentText) → segment(ast, dept-only annotations) →
 * <StaticDocument deptComments=…>. Department filter + search both HIDE
 * non-matching fragments via the same `hiddenBlocks` union as the edit screen.
 */

import React, { useEffect, useMemo, useRef, useState } from "react";
import { Box, Paper, debounce } from "@mui/material";

import { useAppDispatch, useAppSelector } from "../../../../app/hooks";
import { loadUrsOfferNotes, selectAllUrsOfferNotes } from "../../../ursOffer/ursOfferNotesSlice";
import { selectAllDeptAssignments } from "../importedDocDepartmentsSlice";
import { loadDiscussions, selectThreadSummaries } from "../discussionsSlice";
import {
    CompanyDepartmentDetails,
    loadCompanyDepartments,
} from "../../../administration/companyDepartments/companyDepartmentsSlice";
import { ImportedDocumentElement } from "../importedDocumentsApi";

import { selectHiddenParts } from "../importedDocHiddenPartsSlice";

import { parse, nodeText } from "./hast";
import { segment, collectBlockKeys } from "./segmenter";
import { applyTableVisibility } from "./tableVisibility";
import { filterTableRowsByDept } from "./deptRowFilter";
import { buildAnnotationLookup, commentAuthorByKey } from "./annotationAdapter";
import StaticDocument from "./StaticDocument";
import {
    runHighlightSearch,
    clearHighlights,
    matchesSearch,
    filterTableRowsBySearch,
} from "./reviewSearch";
import { AssessmentPanel } from "../components/AssessmentPanel";

const CONTENT_SELECTOR = "[data-doc-content]";

interface Props {
    docId: number;
    /** Document GUID — the discussion's relatedObjectId. */
    docGuid: string;
    elements: ImportedDocumentElement[];
    gutterWidth?: number;
    deptGutterWidth?: number;
    sidePanelWidth?: number;
}

export const AssessDocumentReview: React.FC<Props> = ({
    docId,
    docGuid,
    elements,
    gutterWidth = 200,
    deptGutterWidth = 280,
    sidePanelWidth = 500,
}) => {
    const dispatch = useAppDispatch();
    const gridRef = useRef<HTMLDivElement>(null);

    const notes = useAppSelector(selectAllUrsOfferNotes);
    const deptAssignments = useAppSelector(selectAllDeptAssignments);
    const deptComments = useAppSelector(selectThreadSummaries);
    const hiddenParts = useAppSelector(selectHiddenParts);

    const [departments, setDepartments] = useState<CompanyDepartmentDetails[]>([]);
    // Assess mode stays single-select — a comment thread belongs to one fragment.
    const [selectedKey, setSelectedKey] = useState<string | null>(null);
    const selectedKeySet = useMemo(() => (selectedKey ? new Set([selectedKey]) : null), [selectedKey]);
    const [selectedDeptIds, setSelectedDeptIds] = useState<number[]>([]); // empty = all
    const [search, setSearch] = useState("");
    // Row filtering re-segments the whole document, so it (and the block hide
    // and highlight) follow a debounced copy of the input, not every keystroke.
    const [appliedSearch, setAppliedSearch] = useState("");

    useEffect(() => {
        dispatch(loadCompanyDepartments())
            .unwrap()
            .then((d) => setDepartments([...d].sort((a, b) => a.displayOrder - b.displayOrder)))
            .catch(console.error);
    }, [dispatch]);

    useEffect(() => {
        dispatch(loadUrsOfferNotes({ docId }));
    }, [dispatch, docId]);

    useEffect(() => {
        if (docGuid) dispatch(loadDiscussions({ relatedObjectId: docGuid }));
    }, [dispatch, docGuid]);

    // blockKey → assigned department id (committed assignments; assessors don't
    // change assignments, only comment).
    const deptIdByKey = useMemo(() => {
        const m: Record<string, number> = {};
        for (const a of deptAssignments) {
            if (a.companyDepartmentId != null) m[a.blockKey] = a.companyDepartmentId;
        }
        return m;
    }, [deptAssignments]);

    // Saved notes + dept assignments: the segmenter fills `row.gutter` (note
    // icon + comment sticker, col 2) and `row.dept` (dept badge + comment).
    // Pending note edits are ignored on purpose — assessors see only what the
    // reviewer saved, never a draft left in Redux by the edit screen.
    const annotations = useMemo(() => {
        const noteByKey: Record<string, string> = {};
        const commentsByKey: Record<string, string> = {};
        for (const nh of notes) {
            const key = String(nh.blockKey);
            if (nh.currentNote?.note) noteByKey[key] = nh.currentNote.note;
            if (nh.currentNote?.comment) commentsByKey[key] = nh.currentNote.comment;
        }
        return buildAnnotationLookup({ noteByKey, commentsByKey, deptIdByKey });
    }, [notes, deptIdByKey]);

    const commentAuthors = useMemo(() => commentAuthorByKey(notes, {}), [notes]);

    const { nodes: rawNodes, elementIds } = useMemo(() => {
        const ns: ReturnType<typeof parse> = [];
        const ids: Array<string | undefined> = [];
        for (const el of elements) {
            for (const n of parse(el.contentText ?? "")) {
                ns.push(n);
                ids.push(String(el.id));
            }
        }
        return { nodes: ns, elementIds: ids };
    }, [elements]);
    // Three transforms, all preserving top-level node identity so `elementIds`
    // stays aligned:
    //  1. Table columns and rows the reviewer deleted are removed outright — the
    //     assessor sees the same document the reviewer does. Assess has no
    //     advanced mode, so there is no toolbar and no way to change the choice
    //     from here. Deleted BLOCKS are dropped further down, via hiddenBlocks.
    //  2. With a department filter active, drop table rows not stamped for a
    //     selected department (header rows kept), so a filtered table shows only
    //     the relevant rows instead of the whole table with the rest dimmed.
    //  3. Search does the same with the rows that don't contain the query
    //     (a table whose only hit is its header stays whole).
    const nodes = useMemo(
        () =>
            filterTableRowsBySearch(
                filterTableRowsByDept(
                    applyTableVisibility(rawNodes, elementIds, hiddenParts, "remove"),
                    deptIdByKey,
                    selectedDeptIds,
                ),
                appliedSearch,
            ),
        [rawNodes, elementIds, hiddenParts, deptIdByKey, selectedDeptIds, appliedSearch],
    );
    const plan = useMemo(() => segment(nodes, annotations, elementIds), [nodes, annotations, elementIds]);

    const deptLookup = useMemo(
        () => Object.fromEntries(departments.map((d) => [d.id, { code: d.code, color: d.color }])),
        [departments],
    );

    // Search hides non-matching top-level blocks (same as the edit screen).
    const searchHidden = useMemo(() => {
        if (!appliedSearch) return null;
        const hidden = new Set<number>();
        plan.content.forEach((block, i) => {
            if (!matchesSearch(nodeText(block.node), appliedSearch)) hidden.add(i);
        });
        return hidden;
    }, [appliedSearch, plan]);

    // Department filter hides blocks none of whose keys belong to a selected
    // department (mirrors StaticDocumentReview's note filterHidden, keyed on dept).
    const filterHidden = useMemo(() => {
        if (selectedDeptIds.length === 0) return null;
        const sel = new Set(selectedDeptIds);
        const passKeys = new Set(
            Object.keys(deptIdByKey).filter((k) => sel.has(deptIdByKey[k])),
        );
        const hidden = new Set<number>();
        plan.content.forEach((block, i) => {
            if (!collectBlockKeys([block.node]).some((k) => passKeys.has(k))) hidden.add(i);
        });
        return hidden;
    }, [selectedDeptIds, deptIdByKey, plan]);

    // Blocks the reviewer deleted, resolved to plan indices — the same union the
    // edit screen builds, minus its advanced mode (which keeps them visible so
    // they can be restored). Here they are simply gone.
    const deletedBlocks = useMemo(() => {
        if (hiddenParts.blockIds.size === 0) return null;
        const out = new Set<number>();
        plan.content.forEach((block, i) => {
            if (block.blockId && hiddenParts.blockIds.has(block.blockId)) out.add(i);
        });
        return out;
    }, [hiddenParts, plan]);

    const hiddenBlocks = useMemo(() => {
        if (!searchHidden && !filterHidden && !deletedBlocks) return null;
        const out = new Set<number>();
        searchHidden?.forEach((i) => out.add(i));
        filterHidden?.forEach((i) => out.add(i));
        deletedBlocks?.forEach((i) => out.add(i));
        return out;
    }, [searchHidden, filterHidden, deletedBlocks]);

    const applySearch = useMemo(() => debounce(setAppliedSearch, 250), []);
    useEffect(() => () => applySearch.clear(), [applySearch]);

    const onSearchChange = (value: string) => {
        setSearch(value);
        applySearch(value);
    };

    // Paint after the filtered rows have committed — highlighting before the
    // re-render would put ranges on DOM nodes that are about to be replaced.
    useEffect(() => {
        if (gridRef.current) runHighlightSearch(gridRef.current, appliedSearch, CONTENT_SELECTOR);
    }, [appliedSearch, plan]);

    useEffect(() => () => clearHighlights(), []);

    return (
        <Box sx={{ display: "flex", gap: 2, height: "calc(100vh - 300px)" }}>
            <Paper variant="outlined" sx={{ flex: 1, borderRadius: 2, overflow: "auto", p: 2 }}>
                <StaticDocument
                    plan={plan}
                    gridRef={gridRef}
                    gutterWidth={gutterWidth}
                    deptGutterWidth={deptGutterWidth}
                    docLinkGutterWidth={0}
                    departments={deptLookup}
                    selectedKeys={selectedKeySet}
                    onSelect={(key) => setSelectedKey(key)}
                    hiddenBlocks={hiddenBlocks}
                    commentAuthors={commentAuthors}
                    deptComments={deptComments}
                />
            </Paper>

            <Paper
                variant="outlined"
                sx={{ width: sidePanelWidth, flexShrink: 0, borderRadius: 2, overflow: "auto", p: 2 }}
            >
                <AssessmentPanel
                    relatedObjectId={docGuid}
                    selectedKey={selectedKey}
                    departments={departments}
                    deptIdByKey={deptIdByKey}
                    selectedDeptIds={selectedDeptIds}
                    onSelectedDeptIdsChange={setSelectedDeptIds}
                    search={search}
                    onSearchChange={onSearchChange}
                />
            </Paper>
        </Box>
    );
};

export default AssessDocumentReview;
